import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import Sidebar from '../Dashboard/Sidebar';
import Loader from '../layouts/Loader';
import DeleteConfirmModal from '../layouts/DeleteConfirmModal';
import AppTimePicker from '../common/AppTimePicker';
import {
    getDietTemplates,
    createDietTemplate,
    updateDietTemplate,
    deleteDietTemplate,
    getAllPatientDiets,
    createPatientDiet
} from '../../actions/dietActions';
import { getAdminPatients } from '../../actions/patientActions';
import {
    clearTemplateCreated,
    clearTemplateUpdated,
    clearTemplateDeleted,
    clearDietError
} from '../../slices/dietSlice';
import { downloadDietPDF, downloadDietPDFTamil, printDiet } from '../../utils/dietPdf';
import { translateDietToTamil } from '../../utils/tamilTranslate';
import formatDate from '../../utils/formatDate';
import { getAllStaff } from '../../actions/staffActions';

export default function CommonDietList() {
    const dispatch = useDispatch();

    const { user } = useSelector(state => state.authState);
    const { staffList } = useSelector(state => state.staffState);
    const { patients = [] } = useSelector(state => state.patientsState);
    const doctors = (staffList || []).filter(
        s => s.designation && (
            s.designation.toLowerCase().includes('doctor') ||
            s.designation.toLowerCase().includes('bnys') ||
            s.designation.toLowerCase().includes('physician')
        )
    );
    const defaultDoctor = doctors.length > 0 ? doctors[0].name : (user?.name || '');

    const {
        templates = [],
        allPatientDiets = [],
        loading,
        error,
        isTemplateCreated,
        isTemplateUpdated,
        isTemplateDeleted
    } = useSelector(state => state.dietState);

    // Active View Tab: 'all' (Unified Diet Hub) vs 'templates' (Clinic Master Library) vs 'prescribed' (Patient Prescriptions)
    const [activeTab, setActiveTab] = useState('all');

    // Unified Search
    const [searchTerm, setSearchTerm] = useState('');

    // Modals
    const [showModal, setShowModal] = useState(false);
    const [editingTemplate, setEditingTemplate] = useState(null);
    const [previewTemplate, setPreviewTemplate] = useState(null);
    const [deleteModal, setDeleteModal] = useState({ show: false, id: null, name: '' });
    const [deletedTemplateIds, setDeletedTemplateIds] = useState([]);

    // 🌟 Use for Patient Modal State
    const [usePatientModal, setUsePatientModal] = useState({
        show: false,
        diet: null,
        patientId: '',
        patientName: '',
        patientCode: '',
        phone: '',
        age: '',
        gender: '',
        doctor: defaultDoctor,
        doctorDesignation: 'BNYS',
        date: new Date().toISOString().split('T')[0]
    });
    const [patientSearchTerm, setPatientSearchTerm] = useState('');
    const [savingToPatient, setSavingToPatient] = useState(false);

    // Form fields
    const [name, setName] = useState('');
    const [condition, setCondition] = useState('');
    const [doctor, setDoctor] = useState(defaultDoctor);
    const [doctorDesignation, setDoctorDesignation] = useState('BNYS');
    const [guidelines, setGuidelines] = useState('');

    const handleDoctorChange = (selectedName) => {
        setDoctor(selectedName);
        const match = (staffList || []).find(s => s.name === selectedName);
        if (match) {
            let qual = match.qualification;
            if (!qual && match.designation && match.designation.includes('(')) {
                qual = match.designation.split('(')[1].replace(')', '').trim();
            }
            setDoctorDesignation(qual || 'BNYS');
        } else {
            setDoctorDesignation('BNYS');
        }
    };
    const initialSlots = useMemo(() => [
        { id: 'slot-1', time: '07:00 AM', title: 'Morning Detox', items: 'Warm lemon honey water or Amla juice', instructions: 'Drink on an empty stomach' },
        { id: 'slot-2', time: '08:30 AM', title: 'Breakfast', items: 'Sprouted grains, seasonal fruits, boiled vegetables', instructions: 'Chew well, avoid tea/coffee immediately after' },
        { id: 'slot-3', time: '01:00 PM', title: 'Lunch', items: 'Brown rice / Millet, fresh vegetable salad, dal, buttermilk', instructions: 'Eat in moderation, light digestive meal' },
        { id: 'slot-4', time: '05:00 PM', title: 'Evening Herbal Tea', items: 'Herbal tea / green tea with roasted nuts/seeds', instructions: 'No refined sugar' },
        { id: 'slot-5', time: '07:30 PM', title: 'Dinner', items: 'Vegetable soup, steamed vegetables or light khichdi', instructions: 'Finish dinner at least 2 hours before sleep' }
    ], []);
    const [slots, setSlots] = useState(initialSlots);

    const [isTranslating, setIsTranslating] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);

    // Fetch master templates, all prescribed diets, staff, and clinic patients on mount
    useEffect(() => {
        dispatch(getDietTemplates());
        dispatch(getAllPatientDiets());
        dispatch(getAllStaff());
        dispatch(getAdminPatients());
    }, [dispatch]);

    // Handle feedback notifications
    useEffect(() => {
        if (isTemplateCreated) {
            toast.success('Common diet plan created successfully!');
            dispatch(clearTemplateCreated());
            closeFormModal();
            dispatch(getDietTemplates());
        }
        if (isTemplateUpdated) {
            toast.success('Common diet plan updated successfully!');
            dispatch(clearTemplateUpdated());
            closeFormModal();
            dispatch(getDietTemplates());
        }
        if (isTemplateDeleted) {
            dispatch(clearTemplateDeleted());
            setDeleteModal({ show: false, id: null, name: '' });
        }
        if (error) {
            toast.error(error);
            dispatch(clearDietError());
        }
    }, [isTemplateCreated, isTemplateUpdated, isTemplateDeleted, error, dispatch]);

    const openCreateModal = () => {
        setEditingTemplate(null);
        setName('');
        setCondition('');
        setDoctor(defaultDoctor);
        setDoctorDesignation('BNYS');
        setGuidelines('1. Drink 2.5 to 3 liters of warm water throughout the day.\n2. Avoid white sugar, deep fried foods, and refined flour.\n3. Practice daily 30 minutes of walking or gentle yoga.\n4. Maintain regular meal timings.');
        setSlots(initialSlots);
        setShowModal(true);
    };

    const openEditModal = (template) => {
        setEditingTemplate(template);
        setName(template.name || template.title || '');
        setCondition(template.condition || '');
        setDoctor(template.doctor || defaultDoctor);
        setDoctorDesignation(template.doctorDesignation || 'BNYS');
        setGuidelines(template.guidelines || '');

        let parsedSlots = template.slots;
        if (typeof parsedSlots === 'string') {
            try { parsedSlots = JSON.parse(parsedSlots); } catch (e) { parsedSlots = []; }
        }
        if (Array.isArray(parsedSlots) && parsedSlots.length > 0) {
            setSlots(parsedSlots.map((s, idx) => ({
                id: s.id || `slot-${idx + 1}-${Date.now()}`,
                time: s.time || '',
                title: s.title || `Slot ${idx + 1}`,
                items: s.items || '',
                instructions: s.instructions || ''
            })));
        } else {
            setSlots(initialSlots);
        }
        setShowModal(true);
    };

    const closeFormModal = () => {
        setShowModal(false);
        setEditingTemplate(null);
    };

    // Slot Handlers
    const handleSlotChange = (index, field, value) => {
        setSlots(prev => {
            const next = [...prev];
            next[index] = { ...next[index], [field]: value };
            return next;
        });
    };

    const handleAddSlot = () => {
        setSlots(prev => [
            ...prev,
            {
                id: `slot-${Date.now()}`,
                time: '',
                title: '',
                items: '',
                instructions: ''
            }
        ]);
    };

    const handleRemoveSlot = (index) => {
        if (slots.length <= 1) {
            toast.warn('At least one meal slot is required');
            return;
        }
        setSlots(prev => prev.filter((_, i) => i !== index));
    };

    // Auto-Translate Form to Tamil
    const handleTranslateToTamil = async () => {
        const hasContent = name.trim() || condition.trim() || guidelines.trim() || slots.some(s => s.title.trim() || s.items.trim());
        if (!hasContent) {
            toast.warn('Please enter some diet details first to translate');
            return;
        }

        setIsTranslating(true);
        try {
            const translated = await translateDietToTamil({
                title: name,
                condition,
                slots,
                guidelines
            });

            if (translated) {
                if (translated.title) setName(translated.title);
                if (translated.condition) setCondition(translated.condition);
                if (Array.isArray(translated.slots) && translated.slots.length > 0) {
                    setSlots(translated.slots);
                }
                if (translated.guidelines) setGuidelines(translated.guidelines);
                toast.success('Diet translated to Tamil! Review or adjust before saving.');
            }
        } catch (err) {
            console.error(err);
            toast.error('Failed to translate diet to Tamil');
        } finally {
            setIsTranslating(false);
        }
    };

    // Submit Template
    const handleSaveTemplate = () => {
        if (!name.trim()) {
            toast.error('Please enter a diet plan title');
            return;
        }

        const payload = {
            name: name.trim(),
            title: name.trim(),
            condition: condition.trim(),
            doctor: doctor.trim(),
            doctorDesignation: (doctorDesignation || 'BNYS').trim(),
            slots: slots,
            guidelines: guidelines.trim()
        };

        if (editingTemplate) {
            const templateId = editingTemplate._id || editingTemplate.id;
            dispatch(updateDietTemplate(templateId, payload));
        } else {
            dispatch(createDietTemplate(payload));
        }
    };

    // Delete Template
    const handleConfirmDelete = async () => {
        if (deleteModal.id) {
            const targetId = String(deleteModal.id);
            setDeletedTemplateIds(prev => [...prev, targetId]);
            if (previewTemplate && String(previewTemplate._id || previewTemplate.id) === targetId) {
                setPreviewTemplate(null);
            }
            if (editingTemplate && String(editingTemplate._id || editingTemplate.id) === targetId) {
                closeFormModal();
            }
            setDeleteModal({ show: false, id: null, name: '' });
            const result = await dispatch(deleteDietTemplate(targetId));
            if (result?.success) {
                toast.success('Common diet plan deleted successfully!');
                dispatch(getDietTemplates());
            } else {
                setDeletedTemplateIds(prev => prev.filter(id => id !== targetId));
                toast.error(result?.error || 'Failed to delete common diet plan');
            }
        }
    };

    // Download handlers (WITHOUT patient name)
    const handleDownloadEnglish = (tmpl) => {
        try {
            downloadDietPDF(tmpl, {});
            toast.success(`Downloaded "${tmpl.name || tmpl.title}" (English)`);
        } catch (err) {
            console.error(err);
            toast.error('Failed to generate English PDF');
        }
    };

    const handleDownloadTamil = async (tmpl) => {
        try {
            setIsDownloading(true);
            toast.info('Generating Tamil Diet PDF...');
            await downloadDietPDFTamil(tmpl, {});
            toast.success(`Downloaded "${tmpl.name || tmpl.title}" (Tamil)`);
        } catch (err) {
            console.error(err);
            toast.error('Failed to generate Tamil PDF');
        } finally {
            setIsDownloading(false);
        }
    };

    const handlePrintCommonDiet = (tmpl, lang = 'en') => {
        try {
            printDiet(tmpl, {}, lang);
        } catch (err) {
            console.error(err);
            toast.error('Failed to open print dialog');
        }
    };

    // 🌟 Unified Diets Collection (Templates + Prescribed Diets)
    const unifiedDiets = useMemo(() => {
        const activeTemplates = (templates || []).filter(t => !deletedTemplateIds.includes(String(t._id || t.id))).map(t => ({
            ...t,
            _dietSource: 'template',
            displayTitle: t.name || t.title,
            sortDate: t.createdAt || '1970-01-01'
        }));

        const activePrescribed = (allPatientDiets || []).map(p => ({
            ...p,
            _dietSource: 'prescribed',
            displayTitle: p.title,
            sortDate: p.date || p.createdAt || '1970-01-01'
        }));

        return [...activeTemplates, ...activePrescribed];
    }, [templates, allPatientDiets, deletedTemplateIds]);

    // Unified Filtered Diets
    const filteredDiets = useMemo(() => {
        let base = [];
        if (activeTab === 'templates') {
            base = unifiedDiets.filter(d => d._dietSource === 'template');
        } else if (activeTab === 'prescribed') {
            base = unifiedDiets.filter(d => d._dietSource === 'prescribed');
        } else {
            base = unifiedDiets;
        }

        if (!searchTerm.trim()) return base;
        const q = searchTerm.toLowerCase();

        return base.filter(d => {
            const matchTitle = Boolean(d.displayTitle && d.displayTitle.toLowerCase().includes(q));
            const matchCond = Boolean(d.condition && d.condition.toLowerCase().includes(q));
            const matchDoc = Boolean(d.doctor && d.doctor.toLowerCase().includes(q));
            const matchPatientName = Boolean(d.patient?.name && d.patient.name.toLowerCase().includes(q));
            const matchPatientId = Boolean(d.patient?.patientId && d.patient.patientId.toLowerCase().includes(q));
            const matchGuidelines = Boolean(d.guidelines && d.guidelines.toLowerCase().includes(q));
            let matchSlots = false;
            let slotList = d.slots;
            if (typeof slotList === 'string') {
                try { slotList = JSON.parse(slotList); } catch (e) { slotList = []; }
            }
            if (Array.isArray(slotList)) {
                matchSlots = slotList.some(s =>
                    (s.title && s.title.toLowerCase().includes(q)) ||
                    (s.items && s.items.toLowerCase().includes(q))
                );
            }
            return matchTitle || matchCond || matchDoc || matchPatientName || matchPatientId || matchGuidelines || matchSlots;
        });
    }, [unifiedDiets, activeTab, searchTerm]);

    // 🌟 Handlers for "Use for Patient" Modal
    const openUseForPatientModal = (dietItem) => {
        let initialDoctor = dietItem.doctor || defaultDoctor;
        let initialQual = 'BNYS';
        const matchDoc = (staffList || []).find(s => s.name === initialDoctor);
        if (matchDoc) {
            initialQual = matchDoc.qualification || (matchDoc.designation && matchDoc.designation.includes('(') ? matchDoc.designation.split('(')[1].replace(')', '').trim() : 'BNYS');
        }

        setUsePatientModal({
            show: true,
            diet: dietItem,
            patientId: dietItem.patient?.id || dietItem.patient?._id || '',
            patientName: dietItem.patient?.name || '',
            patientCode: dietItem.patient?.patientId || '',
            phone: dietItem.patient?.phone || '',
            age: dietItem.patient?.age || '',
            gender: dietItem.patient?.gender || '',
            doctor: initialDoctor,
            doctorDesignation: initialQual || 'BNYS',
            date: new Date().toISOString().split('T')[0]
        });
        setPatientSearchTerm(dietItem.patient?.name || '');
    };

    const handleSelectPatientForModal = (p) => {
        setUsePatientModal(prev => ({
            ...prev,
            patientId: p.id || p._id || '',
            patientName: p.name || '',
            patientCode: p.patientId || '',
            phone: p.phone || '',
            age: p.age || '',
            gender: p.gender || ''
        }));
        setPatientSearchTerm(p.name || '');
    };

    const handleSendWhatsAppFromModal = (language = 'en') => {
        if (!usePatientModal.diet) return;
        const diet = usePatientModal.diet;
        const patientName = usePatientModal.patientName.trim() || 'Valued Patient';
        const cleanPhone = String(usePatientModal.phone || '').replace(/[^0-9]/g, '');
        let formattedPhone = cleanPhone;
        if (formattedPhone.startsWith('0') && formattedPhone.length === 11) {
            formattedPhone = '91' + formattedPhone.slice(1);
        } else if (formattedPhone.length === 10) {
            formattedPhone = '91' + formattedPhone;
        }

        const baseUrl = window.location.origin;
        const publicToken = diet.publicToken || diet._id || diet.id;
        const publicDietLink = `${baseUrl}/diet/view/${publicToken}${language === 'ta' ? '?lang=ta' : ''}`;
        const docPrefix = usePatientModal.doctor ? (usePatientModal.doctor.startsWith('Dr') ? usePatientModal.doctor : `Dr. ${usePatientModal.doctor}`) : 'Green Naturopathy Team';
        const docDegree = usePatientModal.doctorDesignation ? ` (${usePatientModal.doctorDesignation})` : ', BNYS';
        const docFullName = `${docPrefix}${docDegree}`;
        const conditionName = diet.condition || diet.title || diet.name || 'Holistic Diet Plan';

        let messageBody = '';
        if (language === 'ta') {
            messageBody = `வணக்கம் ${patientName},\n\n${docFullName} (Green Naturopathy) உங்களுக்கான உணவு முறை மற்றும் ஊட்டச்சத்து அட்டவணையை (${conditionName}) பரிந்துரைத்துள்ளார்.\n\nமுழு உணவு அட்டவணையை காண லிங்க்:\n${publicDietLink}\n\n"உணவே மருந்து, மருந்தே உணவு"\nகிரீன் நேச்சுரோபதி மருத்துவமனை`;
        } else {
            messageBody = `Dear ${patientName},\n\n${docFullName} from Green Naturopathy has prescribed your personalized Diet & Nutrition Plan (${conditionName}).\n\nView & download your official diet chart here:\n${publicDietLink}\n\n"Let food be thy medicine and medicine be thy food."\nGreen Naturopathy Holistic Care`;
        }

        const encoded = encodeURIComponent(messageBody);
        const whatsappUrl = formattedPhone
            ? `https://api.whatsapp.com/send?phone=${formattedPhone}&text=${encoded}`
            : `https://api.whatsapp.com/send?text=${encoded}`;

        window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
    };

    const handleDownloadPDFFromModal = async (language = 'en') => {
        if (!usePatientModal.diet) return;
        const diet = {
            ...usePatientModal.diet,
            doctor: usePatientModal.doctor,
            doctorDesignation: usePatientModal.doctorDesignation,
            date: usePatientModal.date
        };
        const patientObj = {
            name: usePatientModal.patientName.trim(),
            patientId: usePatientModal.patientCode.trim(),
            phone: usePatientModal.phone.trim(),
            age: usePatientModal.age,
            gender: usePatientModal.gender
        };

        if (language === 'ta') {
            setIsDownloading(true);
            toast.info('Generating Tamil Diet PDF with patient details...');
            try {
                await downloadDietPDFTamil(diet, patientObj);
                toast.success(`Downloaded Tamil PDF for ${patientObj.name || 'Patient'}`);
            } catch (err) {
                console.error(err);
                toast.error('Failed to generate Tamil PDF');
            } finally {
                setIsDownloading(false);
            }
        } else {
            try {
                downloadDietPDF(diet, patientObj);
                toast.success(`Downloaded English PDF for ${patientObj.name || 'Patient'}`);
            } catch (err) {
                console.error(err);
                toast.error('Failed to generate English PDF');
            }
        }
    };

    const handleSaveToPatientChart = async () => {
        if (!usePatientModal.patientId) {
            toast.warn('Please select a registered patient from the search list to assign into their medical record');
            return;
        }
        const diet = usePatientModal.diet;
        let slotList = diet.slots;
        if (typeof slotList === 'string') {
            try { slotList = JSON.parse(slotList); } catch (e) { slotList = []; }
        }
        const payload = {
            title: diet.title || diet.name,
            condition: diet.condition || '',
            doctor: usePatientModal.doctor || defaultDoctor,
            doctorDesignation: usePatientModal.doctorDesignation || 'BNYS',
            date: usePatientModal.date || new Date().toISOString().split('T')[0],
            dietType: 'digital',
            slots: Array.isArray(slotList) ? slotList : [],
            guidelines: diet.guidelines || diet.notes || ''
        };

        setSavingToPatient(true);
        const res = await dispatch(createPatientDiet(usePatientModal.patientId, payload));
        setSavingToPatient(false);
        if (res?.success) {
            toast.success(`Diet plan successfully assigned and saved to ${usePatientModal.patientName}'s record!`);
            dispatch(getAllPatientDiets());
            setUsePatientModal(prev => ({ ...prev, show: false }));
        } else {
            toast.error(res?.error || 'Failed to save diet to patient record');
        }
    };

    const handleSaveAsTemplateFromPrescribed = async (item) => {
        let slotList = item.slots;
        if (typeof slotList === 'string') {
            try { slotList = JSON.parse(slotList); } catch (e) { slotList = []; }
        }
        const payload = {
            name: item.title,
            title: item.title,
            condition: item.condition,
            doctor: item.doctor || defaultDoctor,
            doctorDesignation: item.doctorDesignation || 'BNYS',
            slots: Array.isArray(slotList) ? slotList : [],
            guidelines: item.guidelines || ''
        };
        await dispatch(createDietTemplate(payload));
    };

    return (
        <div className="row g-0">
            <div className="col-12 col-md-2"><Sidebar /></div>
            <div className="col-12 col-md-10 main-content">
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                    <div>
                        <h1 className="mb-0 fw-bold" style={{ fontSize: '1.5rem', color: '#1b4d3e' }}>
                            <i className="fa fa-apple-alt me-2 text-success"></i> Naturopathy Diets & Nutrition Hub
                        </h1>
                        <p className="mb-0 mt-1" style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>
                            Access clinic common presets or reuse any diet plan for any patient with one click
                        </p>
                    </div>
                    <button
                        className="btn btn-success fw-semibold shadow-sm"
                        style={{ backgroundColor: '#1b4d3e', borderColor: '#1b4d3e' }}
                        onClick={openCreateModal}
                    >
                        <i className="fa fa-plus me-2"></i> Add Common Diet Preset
                    </button>
                </div>

                {/* View Mode Navigation Tabs */}
                <div className="d-flex gap-2 mb-4 border-bottom pb-3 flex-wrap">
                    <button
                        className={`btn fw-bold px-3 py-2 ${activeTab === 'all' ? 'btn-success text-white shadow-sm' : 'btn-light text-muted'}`}
                        style={activeTab === 'all' ? { backgroundColor: '#1b4d3e', borderColor: '#1b4d3e', borderRadius: '8px' } : { borderRadius: '8px' }}
                        onClick={() => setActiveTab('all')}
                    >
                        <i className="fa fa-th-large me-2"></i> All Diets ({unifiedDiets.length})
                    </button>
                    <button
                        className={`btn fw-bold px-3 py-2 ${activeTab === 'templates' ? 'btn-success text-white shadow-sm' : 'btn-light text-muted'}`}
                        style={activeTab === 'templates' ? { backgroundColor: '#1b4d3e', borderColor: '#1b4d3e', borderRadius: '8px' } : { borderRadius: '8px' }}
                        onClick={() => setActiveTab('templates')}
                    >
                        <i className="fa fa-book-open me-2"></i> Clinic Master Presets ({templates.length})
                    </button>
                    <button
                        className={`btn fw-bold px-3 py-2 ${activeTab === 'prescribed' ? 'btn-success text-white shadow-sm' : 'btn-light text-muted'}`}
                        style={activeTab === 'prescribed' ? { backgroundColor: '#1b4d3e', borderColor: '#1b4d3e', borderRadius: '8px' } : { borderRadius: '8px' }}
                        onClick={() => setActiveTab('prescribed')}
                    >
                        <i className="fa fa-user-friends me-2"></i> Patient Prescriptions ({allPatientDiets.length})
                    </button>
                </div>

                {/* Filter and Search Bar */}
                <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: '12px' }}>
                    <div className="card-body p-3">
                        <div className="row g-3 align-items-center">
                            <div className="col-12 col-md-7">
                                <div className="input-group">
                                    <span className="input-group-text bg-white border-end-0">
                                        <i className="fa fa-search text-muted"></i>
                                    </span>
                                    <input
                                        type="text"
                                        className="form-control border-start-0 ps-0"
                                        placeholder="Search by diet title, health condition, food ingredients, doctor, or patient name..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                    {searchTerm && (
                                        <button
                                            className="btn btn-outline-secondary border-start-0"
                                            onClick={() => setSearchTerm('')}
                                        >
                                            <i className="fa fa-times"></i>
                                        </button>
                                    )}
                                </div>
                            </div>
                            <div className="col-12 col-md-5 text-md-end text-muted small">
                                Showing <strong>{filteredDiets.length}</strong> of <strong>{unifiedDiets.length}</strong> diet charts
                            </div>
                        </div>
                    </div>
                </div>

                {/* Unified Diet Grid */}
                {loading && unifiedDiets.length === 0 ? (
                    <div className="text-center py-5">
                        <Loader />
                    </div>
                ) : filteredDiets.length === 0 ? (
                    <div className="card border-0 shadow-sm text-center py-5" style={{ borderRadius: '12px' }}>
                        <div className="card-body">
                            <i className="fa fa-clipboard-list fa-3x text-muted mb-3 opacity-50"></i>
                            <h5 className="text-dark fw-bold">No Diet Plans Found</h5>
                            <p className="text-muted small mb-4">
                                {searchTerm ? 'No diet plan matches your search keywords.' : 'Start building your master naturopathy diet library or prescribe from patient profile.'}
                            </p>
                            <button
                                className="btn btn-outline-success fw-semibold px-4"
                                onClick={openCreateModal}
                            >
                                <i className="fa fa-plus me-2"></i> Create Your First Diet Plan
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="row g-3">
                        {filteredDiets.map((dietItem) => {
                            const dietId = dietItem._id || dietItem.id;
                            const isTemplate = dietItem._dietSource === 'template';
                            let slotList = dietItem.slots;
                            if (typeof slotList === 'string') {
                                try { slotList = JSON.parse(slotList); } catch (e) { slotList = []; }
                            }
                            const validSlots = Array.isArray(slotList) ? slotList : [];
                            const patientName = dietItem.patient?.name || '';
                            const patientCode = dietItem.patient?.patientId || '';

                            return (
                                <div key={`${dietItem._dietSource}-${dietId}`} className="col-12 col-lg-6 col-xl-4">
                                    <div
                                        className="card h-100 border-0 shadow-sm"
                                        style={{
                                            borderRadius: '12px',
                                            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                                            overflow: 'hidden'
                                        }}
                                    >
                                        <div
                                            style={{
                                                height: '6px',
                                                background: isTemplate
                                                    ? 'linear-gradient(90deg, #1b4d3e 0%, #2e7d32 100%)'
                                                    : 'linear-gradient(90deg, #0284c7 0%, #0369a1 100%)'
                                            }}
                                        />
                                        <div className="card-body p-4 d-flex flex-column">
                                            <div className="d-flex justify-content-between align-items-start mb-2">
                                                <h5 className="fw-bold text-dark mb-1" style={{ fontSize: '1.15rem' }}>
                                                    {dietItem.displayTitle || 'Untitled Diet Plan'}
                                                </h5>
                                            </div>

                                            {/* Source / Patient / Doctor Badges */}
                                            <div className="mb-2 d-flex flex-wrap gap-1 align-items-center small">
                                                {isTemplate ? (
                                                    <span className="badge bg-success bg-opacity-10 text-success border border-success border-opacity-25 py-1 px-2">
                                                        <i className="fa fa-book-open me-1"></i> Clinic Preset
                                                    </span>
                                                ) : (
                                                    <span className="badge bg-primary bg-opacity-10 text-primary border border-primary border-opacity-25 py-1 px-2">
                                                        <i className="fa fa-user me-1"></i> {patientName} {patientCode ? `(${patientCode})` : ''}
                                                    </span>
                                                )}
                                                <span className="badge bg-light text-muted border py-1 px-2">
                                                    <i className="fa fa-user-md me-1 text-success"></i>
                                                    {dietItem.doctor || 'Dr. Consultant'}
                                                </span>
                                                {dietItem.date && (
                                                    <span className="badge bg-light text-secondary border py-1 px-2">
                                                        <i className="fa fa-calendar-alt me-1"></i>
                                                        {formatDate(dietItem.date)}
                                                    </span>
                                                )}
                                            </div>

                                            <div className="mb-3">
                                                <span className="badge bg-success bg-opacity-10 text-success border border-success border-opacity-25 py-1 px-2">
                                                    <i className="fa fa-heartbeat me-1"></i>
                                                    {dietItem.condition || 'General Naturopathy'}
                                                </span>
                                                <span className="badge bg-light text-muted border ms-2 py-1 px-2">
                                                    <i className="fa fa-clock me-1"></i> {validSlots.length} Meal Slots
                                                </span>
                                            </div>

                                            {/* Meal slots preview */}
                                            <div className="bg-light p-2 rounded mb-3" style={{ fontSize: '0.8rem', minHeight: '68px' }}>
                                                {validSlots.slice(0, 3).map((slot, idx) => (
                                                    <div key={idx} className="d-flex text-truncate py-0.5">
                                                        <span className="fw-bold text-success me-2" style={{ minWidth: '60px' }}>
                                                            {slot.time || `Slot ${idx + 1}`}:
                                                        </span>
                                                        <span className="text-secondary text-truncate">
                                                            {slot.title ? `${slot.title} - ` : ''}{slot.items || 'Recommended foods'}
                                                        </span>
                                                    </div>
                                                ))}
                                                {validSlots.length > 3 && (
                                                    <div className="text-muted fst-italic mt-1" style={{ fontSize: '0.75rem' }}>
                                                        + {validSlots.length - 3} more meal slots
                                                    </div>
                                                )}
                                            </div>

                                            {/* Guidelines preview */}
                                            {dietItem.guidelines && (
                                                <p
                                                    className="text-muted small mb-3 text-truncate"
                                                    title={dietItem.guidelines}
                                                    style={{ fontSize: '0.8rem' }}
                                                >
                                                    <i className="fa fa-info-circle text-success me-1"></i>
                                                    {dietItem.guidelines}
                                                </p>
                                            )}

                                            {/* Action Buttons */}
                                            <div className="mt-auto pt-3 border-top">
                                                {/* Primary Use for Patient Button */}
                                                <div className="d-grid mb-2">
                                                    <button
                                                        className="btn btn-sm btn-success fw-bold text-white shadow-sm"
                                                        style={{ backgroundColor: '#1b4d3e', borderColor: '#1b4d3e' }}
                                                        title="Prescribe, download or send this diet for any patient"
                                                        onClick={() => openUseForPatientModal(dietItem)}
                                                    >
                                                        <i className="fa fa-user-plus me-1"></i> Use for Patient / Send
                                                    </button>
                                                </div>

                                                <div className="d-flex flex-wrap gap-2 justify-content-between align-items-center">
                                                    {/* Secondary Buttons: Preview, Edit/Clone, Delete */}
                                                    <div className="btn-group btn-group-sm">
                                                        <button
                                                            className="btn btn-outline-secondary"
                                                            title="Preview Full Diet Chart"
                                                            onClick={() => setPreviewTemplate(dietItem)}
                                                        >
                                                            <i className="fa fa-eye me-1"></i> View
                                                        </button>
                                                        {isTemplate ? (
                                                            <>
                                                                <button
                                                                    className="btn btn-outline-primary"
                                                                    title="Edit Preset Plan"
                                                                    onClick={() => openEditModal(dietItem)}
                                                                >
                                                                    <i className="fa fa-edit me-1"></i> Edit
                                                                </button>
                                                                <button
                                                                    className="btn btn-outline-danger"
                                                                    title="Delete Preset Plan"
                                                                    onClick={() => setDeleteModal({
                                                                        show: true,
                                                                        id: dietId,
                                                                        name: dietItem.displayTitle
                                                                    })}
                                                                >
                                                                    <i className="fa fa-trash me-1"></i> Delete
                                                                </button>
                                                            </>
                                                        ) : (
                                                            <button
                                                                className="btn btn-outline-success"
                                                                title="Clone as Clinic Master Template"
                                                                onClick={() => handleSaveAsTemplateFromPrescribed(dietItem)}
                                                            >
                                                                <i className="fa fa-plus me-1"></i> Save as Master
                                                            </button>
                                                        )}
                                                    </div>

                                                    {/* Download without name */}
                                                    <div className="d-flex gap-1">
                                                        <button
                                                            className="btn btn-sm btn-outline-secondary fw-semibold"
                                                            title="Download English PDF (General / No Patient Name)"
                                                            onClick={() => handleDownloadEnglish(dietItem)}
                                                        >
                                                            <i className="fa fa-download me-1"></i> No Name
                                                        </button>
                                                        <button
                                                            className="btn btn-sm btn-outline-secondary fw-semibold"
                                                            title="Download Tamil PDF (General / No Patient Name)"
                                                            onClick={() => handleDownloadTamil(dietItem)}
                                                            disabled={isDownloading}
                                                        >
                                                            <i className="fa fa-language me-1"></i> தமிழ்
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* ======================================================== */}
                {/* 🔹 CREATE / EDIT COMMON DIET MODAL                       */}
                {/* ======================================================== */}
                {showModal && (
                    <>
                        <div className="modal-backdrop fade show" style={{ zIndex: 1050 }}></div>
                        <div className="modal fade show d-block" tabIndex="-1" style={{ zIndex: 1055, background: 'rgba(0,0,0,0.5)' }}>
                            <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
                                <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '14px' }}>
                                    <div className="modal-header text-white" style={{ backgroundColor: '#1b4d3e' }}>
                                        <h5 className="modal-title fw-bold">
                                            <i className="fa fa-apple-alt me-2"></i>
                                            {editingTemplate ? 'Edit Common Diet Plan' : 'Create New Common Diet Plan'}
                                        </h5>
                                        <button
                                            type="button"
                                            className="btn-close btn-close-white"
                                            onClick={closeFormModal}
                                        ></button>
                                    </div>

                                    <div className="modal-body p-4 bg-light">
                                        <div className="alert alert-info py-2 px-3 small border-0 shadow-sm mb-3">
                                            <i className="fa fa-info-circle me-1"></i>
                                            Common diet plans are reusable clinic templates. When downloaded from here, they are formatted cleanly <strong>without any patient name</strong>.
                                        </div>

                                        {/* Plan Details */}
                                        <div className="card p-3 mb-3 border-0 shadow-sm">
                                            <div className="row g-3">
                                                <div className="col-12 col-md-5">
                                                    <label className="form-label small fw-bold text-muted">Diet Plan Title *</label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        placeholder="e.g. Diabetic Naturopathy Diet, Weight Loss Diet"
                                                        value={name}
                                                        onChange={(e) => setName(e.target.value)}
                                                    />
                                                </div>
                                                <div className="col-12 col-md-3">
                                                    <label className="form-label small fw-bold text-muted">Health Condition / Target Goal</label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        placeholder="e.g. Diabetes, Obesity, Hypertension, Detox"
                                                        value={condition}
                                                        onChange={(e) => setCondition(e.target.value)}
                                                    />
                                                </div>
                                                <div className="col-12 col-md-4">
                                                    <label className="form-label small fw-bold text-muted">Prepared By / Doctor</label>
                                                    <select
                                                        className="form-select"
                                                        value={doctor}
                                                        onChange={(e) => handleDoctorChange(e.target.value)}
                                                    >
                                                        <option value="">-- Clinic Consultant (BNYS) --</option>
                                                        {doctors.map(d => (
                                                            <option key={d._id || d.id} value={d.name}>
                                                                {d.name.startsWith('Dr') ? d.name : `Dr. ${d.name}`} {d.qualification ? `(${d.qualification})` : (d.designation ? `(${d.designation})` : '(BNYS)')}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Auto-Translate Button */}
                                        <div className="d-flex justify-content-between align-items-center mb-2 px-1">
                                            <h6 className="fw-bold text-dark mb-0">
                                                <i className="fa fa-utensils text-success me-2"></i> Meal Schedule & Timings
                                            </h6>
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-outline-success fw-semibold"
                                                onClick={handleTranslateToTamil}
                                                disabled={isTranslating}
                                            >
                                                {isTranslating ? (
                                                    <>
                                                        <i className="fa fa-spinner fa-spin me-1"></i> Translating...
                                                    </>
                                                ) : (
                                                    <>
                                                        <i className="fa fa-language me-1"></i> Auto-Translate to தமிழ்
                                                    </>
                                                )}
                                            </button>
                                        </div>

                                        {/* Meal Slots Builder */}
                                        <div className="card p-3 mb-3 border-0 shadow-sm">
                                            <div className="d-flex flex-column gap-3">
                                                {slots.map((slot, index) => (
                                                    <div
                                                        key={slot.id || index}
                                                        className="p-3 bg-white border rounded shadow-xs position-relative"
                                                        style={{ borderLeft: '4px solid #1b4d3e !important' }}
                                                    >
                                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                                            <span className="badge bg-success" style={{ backgroundColor: '#1b4d3e' }}>
                                                                Slot #{index + 1}
                                                            </span>
                                                            <button
                                                                type="button"
                                                                className="btn btn-outline-danger btn-sm py-0 px-2"
                                                                title="Remove this slot"
                                                                onClick={() => handleRemoveSlot(index)}
                                                            >
                                                                <i className="fa fa-trash"></i>
                                                            </button>
                                                        </div>

                                                        <div className="row g-2">
                                                            <div className="col-12 col-md-3">
                                                                <label className="form-label small text-muted mb-1">Time (Click to Pick)</label>
                                                                <AppTimePicker
                                                                    value={slot.time}
                                                                    placeholder="Select Time"
                                                                    onChange={(val) => handleSlotChange(index, 'time', val)}
                                                                />
                                                            </div>
                                                            <div className="col-12 col-md-4">
                                                                <label className="form-label small text-muted mb-1">Slot Name</label>
                                                                <input
                                                                    type="text"
                                                                    className="form-control form-control-sm"
                                                                    placeholder="e.g. Morning Detox / Breakfast"
                                                                    value={slot.title}
                                                                    onChange={(e) => handleSlotChange(index, 'title', e.target.value)}
                                                                />
                                                            </div>
                                                            <div className="col-12 col-md-5">
                                                                <label className="form-label small text-muted mb-1">Special Instructions / Notes</label>
                                                                <input
                                                                    type="text"
                                                                    className="form-control form-control-sm"
                                                                    placeholder="e.g. Drink warm, chew slowly"
                                                                    value={slot.instructions}
                                                                    onChange={(e) => handleSlotChange(index, 'instructions', e.target.value)}
                                                                />
                                                            </div>
                                                            <div className="col-12">
                                                                <label className="form-label small text-muted mb-1">Recommended Foods & Nutrition *</label>
                                                                <textarea
                                                                    className="form-control form-control-sm"
                                                                    rows="2"
                                                                    placeholder="List recommended foods, ingredients, juices, fruits, or meal items..."
                                                                    value={slot.items}
                                                                    onChange={(e) => handleSlotChange(index, 'items', e.target.value)}
                                                                ></textarea>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}

                                                <button
                                                    type="button"
                                                    className="btn btn-outline-success btn-sm align-self-start fw-semibold"
                                                    onClick={handleAddSlot}
                                                >
                                                    <i className="fa fa-plus me-1"></i> Add Another Meal Slot
                                                </button>
                                            </div>
                                        </div>

                                        {/* Guidelines & Do's/Don'ts */}
                                        <div className="card p-3 border-0 shadow-sm">
                                            <label className="form-label small fw-bold text-muted mb-1">
                                                Lifestyle Guidelines & Do's / Don'ts (Printed on Diet Chart)
                                            </label>
                                            <textarea
                                                className="form-control"
                                                rows="4"
                                                placeholder="Enter lifestyle tips, water intake guidelines, foods to avoid, exercise rules..."
                                                value={guidelines}
                                                onChange={(e) => setGuidelines(e.target.value)}
                                            ></textarea>
                                        </div>
                                    </div>

                                    <div className="modal-footer bg-white border-0 py-3">
                                        <button
                                            type="button"
                                            className="btn btn-light"
                                            onClick={closeFormModal}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-success fw-semibold px-4"
                                            style={{ backgroundColor: '#1b4d3e', borderColor: '#1b4d3e' }}
                                            onClick={handleSaveTemplate}
                                            disabled={loading}
                                        >
                                            {loading ? (
                                                <>
                                                    <i className="fa fa-spinner fa-spin me-2"></i> Saving...
                                                </>
                                            ) : (
                                                <>
                                                    <i className="fa fa-check me-2"></i> {editingTemplate ? 'Update Diet Plan' : 'Save Common Diet Plan'}
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                )}

                {/* ======================================================== */}
                {/* 🔹 PREVIEW MODAL                                         */}
                {/* ======================================================== */}
                {previewTemplate && (
                    <>
                        <div className="modal-backdrop fade show" style={{ zIndex: 1050 }}></div>
                        <div className="modal fade show d-block" tabIndex="-1" style={{ zIndex: 1055, background: 'rgba(0,0,0,0.5)' }}>
                            <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
                                <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '14px' }}>
                                    <div className="modal-header text-white" style={{ backgroundColor: '#1b4d3e' }}>
                                        <div>
                                            <h5 className="modal-title fw-bold mb-0">
                                                {previewTemplate.name || previewTemplate.title}
                                            </h5>
                                            <small className="opacity-75">
                                                Common Plan (No Patient Details Attached)
                                            </small>
                                        </div>
                                        <button
                                            type="button"
                                            className="btn-close btn-close-white"
                                            onClick={() => setPreviewTemplate(null)}
                                        ></button>
                                    </div>

                                    <div className="modal-body p-4 bg-light">
                                        <div className="bg-white p-3 rounded shadow-sm border mb-3">
                                            <div className="text-success small fw-bold text-uppercase mb-1">Condition / Target</div>
                                            <div className="fs-6 fw-bold text-dark">{previewTemplate.condition || 'General Health & Wellness'}</div>
                                        </div>

                                        <div className="card p-3 border-0 shadow-sm mb-3">
                                            <h6 className="fw-bold text-dark mb-3">
                                                <i className="fa fa-calendar-alt text-success me-2"></i> Meal Schedule
                                            </h6>
                                            <div className="table-responsive">
                                                <table className="table table-bordered table-sm align-middle mb-0">
                                                    <thead className="table-light">
                                                        <tr>
                                                            <th style={{ width: '20%' }}>Time</th>
                                                            <th style={{ width: '25%' }}>Slot</th>
                                                            <th style={{ width: '55%' }}>Recommended Foods & Instructions</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {(() => {
                                                            let list = previewTemplate.slots;
                                                            if (typeof list === 'string') {
                                                                try { list = JSON.parse(list); } catch (e) { list = []; }
                                                            }
                                                            const arr = Array.isArray(list) ? list : [];
                                                            return arr.map((s, idx) => (
                                                                <tr key={idx}>
                                                                    <td className="fw-bold text-success">{s.time || '-'}</td>
                                                                    <td className="fw-semibold">{s.title || `Slot ${idx + 1}`}</td>
                                                                    <td>
                                                                        <div className="text-dark">{s.items || '-'}</div>
                                                                        {s.instructions && (
                                                                            <small className="text-muted fst-italic d-block mt-1">
                                                                                Note: {s.instructions}
                                                                            </small>
                                                                        )}
                                                                    </td>
                                                                </tr>
                                                            ));
                                                        })()}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>

                                        {previewTemplate.guidelines && (
                                            <div className="card p-3 border-0 shadow-sm">
                                                <h6 className="fw-bold text-dark mb-2">
                                                    <i className="fa fa-info-circle text-success me-2"></i> Guidelines & Tips
                                                </h6>
                                                <div className="small text-muted" style={{ whiteSpace: 'pre-line', lineHeight: '1.6' }}>
                                                    {previewTemplate.guidelines}
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <div className="modal-footer bg-white border-0 py-3 d-flex justify-content-between">
                                        <button
                                            type="button"
                                            className="btn btn-light"
                                            onClick={() => setPreviewTemplate(null)}
                                        >
                                            Close
                                        </button>
                                        <div className="d-flex gap-2">
                                            {previewTemplate._dietSource === 'template' && (
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-primary fw-semibold"
                                                    onClick={() => {
                                                        const tmpl = previewTemplate;
                                                        setPreviewTemplate(null);
                                                        openEditModal(tmpl);
                                                    }}
                                                >
                                                    <i className="fa fa-edit me-1"></i> Edit Plan
                                                </button>
                                            )}
                                            <button
                                                type="button"
                                                className="btn btn-success fw-semibold"
                                                onClick={() => handleDownloadEnglish(previewTemplate)}
                                            >
                                                <i className="fa fa-download me-1"></i> Download EN PDF
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-success fw-semibold"
                                                onClick={() => handleDownloadTamil(previewTemplate)}
                                                disabled={isDownloading}
                                            >
                                                <i className="fa fa-language me-1"></i> Download தமிழ் PDF
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-outline-dark"
                                                onClick={() => handlePrintCommonDiet(previewTemplate, 'en')}
                                            >
                                                <i className="fa fa-print me-1"></i> Print
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                )}

                {/* ======================================================== */}
                {/* 🌟 MODAL: USE FOR PATIENT (SELECT / CHANGE PATIENT NAME)  */}
                {/* ======================================================== */}
                {usePatientModal.show && usePatientModal.diet && (
                    <>
                        <div className="modal-backdrop fade show" style={{ zIndex: 1050 }}></div>
                        <div className="modal fade show d-block" tabIndex="-1" style={{ zIndex: 1055, background: 'rgba(0,0,0,0.5)' }}>
                            <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
                                <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '14px' }}>
                                    <div className="modal-header text-white" style={{ backgroundColor: '#1b4d3e' }}>
                                        <h5 className="modal-title fw-bold">
                                            <i className="fa fa-user-check me-2"></i>
                                            Use Diet for Patient / Send
                                        </h5>
                                        <button
                                            type="button"
                                            className="btn-close btn-close-white"
                                            onClick={() => setUsePatientModal(prev => ({ ...prev, show: false }))}
                                        ></button>
                                    </div>

                                    <div className="modal-body p-4 bg-light">
                                        {/* Selected Diet Preview Box */}
                                        <div className="card p-3 mb-3 border-0 shadow-sm" style={{ backgroundColor: '#eef6f2', borderLeft: '4px solid #1b4d3e' }}>
                                            <div className="d-flex justify-content-between align-items-start">
                                                <div>
                                                    <span className="badge bg-success bg-opacity-75 text-white mb-1">
                                                        {usePatientModal.diet._dietSource === 'template' ? 'Clinic Master Preset' : 'Reused from Past Prescription'}
                                                    </span>
                                                    <h5 className="fw-bold text-dark mb-0">
                                                        {usePatientModal.diet.displayTitle || usePatientModal.diet.name || usePatientModal.diet.title}
                                                    </h5>
                                                    <small className="text-muted">
                                                        Condition: <strong>{usePatientModal.diet.condition || 'General Naturopathy'}</strong>
                                                    </small>
                                                </div>
                                                <span className="badge bg-light text-dark border">
                                                    {Array.isArray(usePatientModal.diet.slots) ? usePatientModal.diet.slots.length : 5} Meal Slots
                                                </span>
                                            </div>
                                        </div>

                                        {/* Patient Selection Card */}
                                        <div className="card p-3 mb-3 border-0 shadow-sm">
                                            <h6 className="fw-bold text-success mb-2">
                                                <i className="fa fa-search me-1"></i> 1. Select Registered Clinic Patient
                                            </h6>
                                            <div className="input-group mb-2">
                                                <span className="input-group-text bg-white border-end-0">
                                                    <i className="fa fa-search text-muted"></i>
                                                </span>
                                                <input
                                                    type="text"
                                                    className="form-control border-start-0"
                                                    placeholder="Type patient name, phone number, or GN-ID (e.g. GN-0005)..."
                                                    value={patientSearchTerm}
                                                    onChange={(e) => setPatientSearchTerm(e.target.value)}
                                                />
                                                {patientSearchTerm && (
                                                    <button
                                                        type="button"
                                                        className="btn btn-outline-secondary"
                                                        onClick={() => {
                                                            setPatientSearchTerm('');
                                                            setUsePatientModal(prev => ({
                                                                ...prev,
                                                                patientId: '',
                                                                patientName: '',
                                                                patientCode: '',
                                                                phone: '',
                                                                age: '',
                                                                gender: ''
                                                            }));
                                                        }}
                                                    >
                                                        <i className="fa fa-times"></i> Clear
                                                    </button>
                                                )}
                                            </div>

                                            {/* Matching patients dropdown results */}
                                            {patientSearchTerm.trim() && (
                                                <div className="border rounded bg-white p-2 mb-2" style={{ maxHeight: '180px', overflowY: 'auto' }}>
                                                    {(() => {
                                                        const q = patientSearchTerm.toLowerCase();
                                                        const matches = (patients || []).filter(p =>
                                                            (p.name && p.name.toLowerCase().includes(q)) ||
                                                            (p.patientId && p.patientId.toLowerCase().includes(q)) ||
                                                            (p.phone && p.phone.includes(q))
                                                        ).slice(0, 8);

                                                        if (matches.length === 0) {
                                                            return <div className="text-muted small p-2">No matching clinic patient found. You can enter custom name below.</div>;
                                                        }

                                                        return matches.map(p => (
                                                            <div
                                                                key={p.id || p._id}
                                                                className="p-2 border-bottom d-flex justify-content-between align-items-center hover-bg-light cursor-pointer"
                                                                style={{ cursor: 'pointer' }}
                                                                onClick={() => handleSelectPatientForModal(p)}
                                                            >
                                                                <div>
                                                                    <span className="fw-bold text-dark">{p.name}</span>
                                                                    <span className="badge bg-light text-muted border ms-2">{p.patientId || 'No ID'}</span>
                                                                    {p.phone && <small className="text-muted ms-2">Ph: {p.phone}</small>}
                                                                </div>
                                                                <button type="button" className="btn btn-sm btn-outline-success py-0 px-2">
                                                                    Select
                                                                </button>
                                                            </div>
                                                        ));
                                                    })()}
                                                </div>
                                            )}

                                            {/* Active Patient Details Fields */}
                                            <div className="row g-2 mt-1">
                                                <div className="col-12 col-md-6">
                                                    <label className="form-label small fw-semibold text-muted mb-1">Patient Name</label>
                                                    <input
                                                        type="text"
                                                        className="form-control form-control-sm"
                                                        placeholder="e.g. Ramesh Kumar"
                                                        value={usePatientModal.patientName}
                                                        onChange={(e) => setUsePatientModal(prev => ({ ...prev, patientName: e.target.value }))}
                                                    />
                                                </div>
                                                <div className="col-12 col-md-6">
                                                    <label className="form-label small fw-semibold text-muted mb-1">WhatsApp / Phone Number</label>
                                                    <input
                                                        type="text"
                                                        className="form-control form-control-sm"
                                                        placeholder="e.g. 9876543210"
                                                        value={usePatientModal.phone}
                                                        onChange={(e) => setUsePatientModal(prev => ({ ...prev, phone: e.target.value }))}
                                                    />
                                                </div>
                                            </div>
                                            {usePatientModal.patientId ? (
                                                <div className="mt-2 text-success small">
                                                    <i className="fa fa-check-circle me-1"></i>
                                                    Linked to registered patient <strong>{usePatientModal.patientName}</strong> ({usePatientModal.patientCode})
                                                </div>
                                            ) : (
                                                <div className="mt-2 text-muted small">
                                                    <i className="fa fa-info-circle me-1"></i>
                                                    Custom patient name entered (walk-in / phone consult).
                                                </div>
                                            )}
                                        </div>

                                        {/* Doctor and Date Selection */}
                                        <div className="card p-3 mb-3 border-0 shadow-sm">
                                            <h6 className="fw-bold text-success mb-2">
                                                <i className="fa fa-user-md me-1"></i> 2. Prescribing Doctor & Date
                                            </h6>
                                            <div className="row g-2">
                                                <div className="col-12 col-md-6">
                                                    <label className="form-label small fw-semibold text-muted mb-1">Doctor</label>
                                                    <select
                                                        className="form-select form-select-sm"
                                                        value={usePatientModal.doctor}
                                                        onChange={(e) => {
                                                            const docName = e.target.value;
                                                            const match = (staffList || []).find(s => s.name === docName);
                                                            let qual = match?.qualification || (match?.designation && match.designation.includes('(') ? match.designation.split('(')[1].replace(')', '').trim() : 'BNYS');
                                                            setUsePatientModal(prev => ({
                                                                ...prev,
                                                                doctor: docName,
                                                                doctorDesignation: qual || 'BNYS'
                                                            }));
                                                        }}
                                                    >
                                                        {doctors.map(d => (
                                                            <option key={d._id || d.id} value={d.name}>
                                                                {d.name} ({d.qualification || 'BNYS'})
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div className="col-12 col-md-6">
                                                    <label className="form-label small fw-semibold text-muted mb-1">Date</label>
                                                    <input
                                                        type="date"
                                                        className="form-control form-control-sm"
                                                        value={usePatientModal.date}
                                                        onChange={(e) => setUsePatientModal(prev => ({ ...prev, date: e.target.value }))}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        {/* Action Panel */}
                                        <div className="card p-3 border-0 shadow-sm bg-white">
                                            <h6 className="fw-bold text-dark mb-3">
                                                <i className="fa fa-paper-plane me-1 text-success"></i> 3. Select Action
                                            </h6>
                                            <div className="row g-2">
                                                {/* WhatsApp buttons */}
                                                <div className="col-12 col-md-6">
                                                    <div className="p-2 border rounded bg-light">
                                                        <div className="small fw-bold text-dark mb-2">
                                                            <i className="fab fa-whatsapp text-success me-1"></i> Send to Patient via WhatsApp
                                                        </div>
                                                        <div className="d-flex gap-2">
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-success w-100 fw-semibold"
                                                                style={{ backgroundColor: '#25D366', borderColor: '#25D366' }}
                                                                onClick={() => handleSendWhatsAppFromModal('en')}
                                                            >
                                                                <i className="fab fa-whatsapp me-1"></i> WhatsApp (EN)
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-success w-100 fw-semibold"
                                                                style={{ backgroundColor: '#128C7E', borderColor: '#128C7E' }}
                                                                onClick={() => handleSendWhatsAppFromModal('ta')}
                                                            >
                                                                <i className="fab fa-whatsapp me-1"></i> WhatsApp (தமிழ்)
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Download PDF buttons */}
                                                <div className="col-12 col-md-6">
                                                    <div className="p-2 border rounded bg-light">
                                                        <div className="small fw-bold text-dark mb-2">
                                                            <i className="fa fa-file-pdf text-danger me-1"></i> Download PDF with Patient Details
                                                        </div>
                                                        <div className="d-flex gap-2">
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-outline-success w-100 fw-semibold"
                                                                onClick={() => handleDownloadPDFFromModal('en')}
                                                            >
                                                                <i className="fa fa-download me-1"></i> PDF (EN)
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-outline-success w-100 fw-semibold"
                                                                onClick={() => handleDownloadPDFFromModal('ta')}
                                                                disabled={isDownloading}
                                                            >
                                                                <i className="fa fa-language me-1"></i> PDF (தமிழ்)
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Save to Patient Record Button */}
                                                <div className="col-12 mt-2">
                                                    <button
                                                        type="button"
                                                        className="btn btn-success w-100 fw-bold py-2 shadow-sm"
                                                        style={{ backgroundColor: '#1b4d3e', borderColor: '#1b4d3e' }}
                                                        onClick={handleSaveToPatientChart}
                                                        disabled={!usePatientModal.patientId || savingToPatient}
                                                    >
                                                        {savingToPatient ? (
                                                            <>
                                                                <i className="fa fa-spinner fa-spin me-2"></i> Saving to Patient Record...
                                                            </>
                                                        ) : (
                                                            <>
                                                                <i className="fa fa-save me-2"></i>
                                                                {usePatientModal.patientId
                                                                    ? `Assign & Save to ${usePatientModal.patientName}'s Medical Profile`
                                                                    : 'Assign to Patient Profile (Select a registered patient above)'}
                                                            </>
                                                        )}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="modal-footer bg-white border-0 py-3">
                                        <button
                                            type="button"
                                            className="btn btn-light"
                                            onClick={() => setUsePatientModal(prev => ({ ...prev, show: false }))}
                                        >
                                            Close
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                )}

                {/* ======================================================== */}
                {/* 🔹 DELETE CONFIRM MODAL                                  */}
                {/* ======================================================== */}
                <DeleteConfirmModal
                    show={deleteModal.show}
                    name={deleteModal.name}
                    itemName={deleteModal.name}
                    title="Delete Common Diet Plan"
                    onClose={() => setDeleteModal({ show: false, id: null, name: '' })}
                    onConfirm={handleConfirmDelete}
                />
            </div>
        </div>
    );
}

